import React, { FC } from 'react';

import { DataGroupItemResponse, UserDataGroupResponse } from '@ariksa/abac/api';
import { SearchResponseLevel } from '@ariksa/inventory-core';
import { Box, Center, Wrap } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { customTheme } from 'theme';

import { StackedCell, WithResourceIcon } from 'components/DataDisplay';
import { renderDataTypeTag } from 'components/DataDisplay/DataType/render';
import { normalizeDataTypes } from 'components/DataDisplay/DataType/utils';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import formatNumber from 'components/DataDisplay/Utils/formatNumber';
import { ActionButton, GraphIcon, IconTypes } from 'components/index';
import { MetadataTabs } from 'containers/ActiveCloudResource/types';
import { securityGraphRedirectUrl } from 'containers/Visibility/SecurityGraphNext/utils/securityGraphRedirectUrl';

import { selectDataCatalog } from '../selectors';

import { getDataSourceDetails, getRoles, renderPrivileges } from './utils';

interface Props {
  searchTerm?: string;
}

interface DataSourceRow {
  dataGroup: UserDataGroupResponse;
  item: DataGroupItemResponse;
}

export const DataSourceTable: FC<Props> = ({ searchTerm = '' }) => {
  const { dataGroups } = useSelector(selectDataCatalog);
  const navigate = useNavigate();

  const columns = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Data Source</Box>,
      render: ({ row }: { row: DataSourceRow }) => {
        const details = getDataSourceDetails(row.item);

        return (
          <WithResourceIcon
            resourceType={details.resourceType}
            iconSize="sm"
            iconFilled={false}
            tooltip={details.name}
          >
            <StackedCell
              upper={details.name}
              lower={details.label}
              showUpperTooltip={false}
              showLowerTooltip={false}
            />
          </WithResourceIcon>
        );
      },
    },
    {
      header: 'Privileges',
      render: ({ row }: { row: DataSourceRow }) =>
        renderPrivileges(row.dataGroup),
      align: 'left',
    },
    {
      header: 'Mechanism',
      render: ({ row }: { row: DataSourceRow }) => {
        const roleCount = getRoles(row.dataGroup).length;
        return roleCount
          ? `${roleCount} Snowflake Role${roleCount === 1 ? '' : 's'}`
          : '-';
      },
      align: 'left',
    },
    {
      header: 'Sensitive Records',
      render: ({ row }: { row: DataSourceRow }) => {
        const item = row.item as DataGroupItemResponse & Record<string, any>;
        const count =
          item.sensitive_records ??
          item.total_sensitive_records ??
          item.sensitive_data_count;

        return count == null
          ? '-'
          : typeof count === 'number'
          ? formatNumber(count, 1)
          : count;
      },
    },
    {
      header: 'Sensitivity',
      render: ({ row }: { row: DataSourceRow }) => {
        const categories = Array.from(
          new Set(normalizeDataTypes(row.dataGroup.sensitivity_categories)),
        );

        return categories.length ? (
          <Wrap gap="4px">
            {categories.map(category =>
              renderDataTypeTag(category, { showIcon: false }),
            )}
          </Wrap>
        ) : (
          '-'
        );
      },
    },
    {
      header: 'Data Group',
      render: ({ row }: { row: DataSourceRow }) => (
        <WithResourceIcon
          resourceType={IconTypes.DataGroup}
          color={customTheme.colors.blue['300']}
          iconStyles={{ boxSize: 6 }}
          tooltip={row.dataGroup.name}
        >
          {row.dataGroup.name}
        </WithResourceIcon>
      ),
      align: 'left',
    },
    {
      header: 'Action',
      render: ({ row }: { row: DataSourceRow }) => {
        const details = getDataSourceDetails(row.item);

        return (
          <Center>
            <ActionButton
              label="Show access graph"
              icon={<GraphIcon />}
              onClick={event => {
                event.stopPropagation();
                navigate(
                  securityGraphRedirectUrl({
                    account_id: row.dataGroup.account?.account_id,
                    source_id: details.name,
                    resource_id: details.name,
                    resource_type: details.resourceType,
                    source_resource_uuid: row.item.uuid,
                    map_type: 'identity',
                    source_tab: MetadataTabs.Data,
                    response_level: SearchResponseLevel.Compact,
                  }),
                  { state: row },
                );
              }}
            />
          </Center>
        );
      },
      align: 'center',
    },
  ];

  return (
    <BorderedBox
      flex={1}
      w="full"
      h="full"
      minH={0}
      display="flex"
      flexDirection="column"
      py={4}
      px={6}
    >
      <Table
        columns={columns}
        data={[]}
        //isLoading={dataGroups.isLoading}
        //isError={dataGroups.isError}
        noDataMessage="No data sources available"
      />
    </BorderedBox>
  );
};
